import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconCrossSmall } from '~/ui/core/icons';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Tile, TileContent, TileControlGroup, TileStickyHeader } from '~/ui/core/tile/Tile';
import { TileFooter } from '~/ui/core/tile/TileFooter';
import { HeaderContainer, NavigationButtons } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import { AgentChangeGlimmer } from '../../chat';
import { ContentContainer } from '../../job-architecture-v2';
import { PositionLoading } from '../components/PositionMessage';

export const PositionTileFrame: FunctionComponent<{
    readonly children: ReactNode;
    readonly glimmerEntityId?: string;
    readonly header: ReactNode;
    readonly testId?: string;
}> = ({ children, glimmerEntityId, header, testId }) => (
    <Tile
        className="relative [&_[data-radix-scroll-area-viewport]>div]:block! [&_[data-radix-scroll-area-viewport]>div]:h-full"
        data-testid={testId}
    >
        <AgentChangeGlimmer className="rounded-xxxl" entityId={glimmerEntityId} />
        <ScrollArea scrollFade="vertical" scrollFadeSize="var(--spacing-4xl)">
            <TileContent>
                <ContentContainer>
                    <div className="flex w-full min-w-0 flex-col gap-0">
                        <TileStickyHeader compact={true}>{header}</TileStickyHeader>
                        {children}
                    </div>
                    <div className="mt-auto">
                        <TileFooter />
                    </div>
                </ContentContainer>
            </TileContent>
        </ScrollArea>
    </Tile>
);

export const PositionCloseHeader: FunctionComponent<{ readonly onClose: () => void }> = ({ onClose }) => {
    const translate = useTranslate();

    return (
        <HeaderContainer>
            <NavigationButtons>
                <TileControlGroup>
                    <Button
                        aria-label={translate('common.close')}
                        iconOnly={true}
                        size="sm"
                        variant="secondary"
                        onClick={onClose}
                    >
                        <IconCrossSmall />
                    </Button>
                </TileControlGroup>
            </NavigationButtons>
        </HeaderContainer>
    );
};

export const PositionTileLoading: FunctionComponent<{ readonly onClose: () => void }> = ({ onClose }) => {
    const translate = useTranslate();

    return (
        <PositionTileFrame header={<PositionCloseHeader onClose={onClose} />}>
            <PositionLoading
                className="h-[20rem] w-full"
                label={translate('pages.position-management.detail.loading')}
            />
        </PositionTileFrame>
    );
};
